import React, {useState, useEffect} from 'react';
import {matchPath, useLocation, useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {metrics} from '~/vendor/metrics';
import projectClient from '~/vendor/projectService';
import remoteServices from '~/constants/RemoteServices';
import {GET_IT_BY} from '~/constants/ShippingConstants';

import {
  addDays,
  shortenWeekDay,
  isDateMoreThanTwoWeeksAway
} from '~/utils/date';
import {decision, decide} from '~/utils/decisionTree';
import {postHeaders, rfeCredentialsHeader} from '~/utils/http';
import {ga4event} from '~/utils/analytics';

import {useDispatch, useSelector} from 'react-redux';
import {setOrderOptionsPath, getOrderOptionsPath} from '~/slices/pathSlice';
import {
  waysToOrderLoad,
  waysToOrderOptionUpdate
} from '~/actions/AnalyticsActions';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';

import {serializeProjectConfiguration as serialize} from '~/data/serializers/project';
import {deserializeProject as deserialize} from '~/data/deserializers/project';
import {deserializeQuoteWidget} from '~/data/deserializers/quote';

import {
  getSelectedProduct,
  getSelectedProductSizes,
  isSingleSizeAndStyle,
  allowIndividualShipping
} from '~/selectors/ProductSelectors';
import {
  simplePricingTestEnabled,
  wtoTestEnabled
} from '~/selectors/ConfigSelectors';

import DeliveryMessage from './DeliveryMessage';
import Spinner from '~/components/Spinner';
import OrderOption from './OrderOption';
import {determineShippingOption, checkResponseOkay} from './util';
import {
  ArrowNextIcon,
  Box,
  Button,
  Stack,
  Typography
} from '@customink/pigment-react';

import './index.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export default function OrderOptions({preview}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState({});
  const [firedProjectLoadAnalytics, setFiredProjectLoadAnalytics] =
    useState(false);
  const [fulfillmentPlatform, setFulfillmentPlatform] = useState('BULK');
  const [standardDelivery, setStandardDelivery] = useState();
  const [rushDelivery, setRushDelivery] = useState();
  const [showSpinner, setShowSpinner] = useState(false);

  const individualShipEligible = useSelector(allowIndividualShipping);
  const orderOptionsPath = useSelector(getOrderOptionsPath);
  const product = useSelector(getSelectedProduct);
  const oneSizeOneColor = useSelector(isSingleSizeAndStyle);
  const simplePricingEnabled = useSelector(simplePricingTestEnabled);
  const isWtoTestEnabled = useSelector(wtoTestEnabled);
  const isProductOneSize = useSelector(getSelectedProductSizes)?.includes(
    'One Size'
  );

  const skipSizes = simplePricingEnabled && oneSizeOneColor;
  const orderOptionsOrWtoTest =
    matchPath('/next/order-options', location.pathname) ||
    (wtoTestEnabled && matchPath('/next', location.pathname));

  useEffect(() => {
    setShowSpinner(true);
    projectClient.getCurrentProject().then(project => {
      setShowSpinner(false);
      if (Object.keys(project).length !== 0) {
        const deserializedProject = deserialize(project);
        setProject({
          ...deserializedProject,
          shipping: deserializedProject.shipping ?? 'ORGANIZER',
          sizes: deserializedProject.sizes ?? 'ORGANIZER',
          payment: deserializedProject.payment ?? 'ORGANIZER'
        });
      } else {
        setProject({
          id: null,
          shipping: 'ORGANIZER',
          sizes: 'ORGANIZER',
          payment: 'ORGANIZER'
        });
      }
    });
  }, [preview]);

  useEffect(() => {
    if (
      !firedProjectLoadAnalytics &&
      orderOptionsOrWtoTest &&
      project.shipping &&
      project.sizes &&
      project.payment
    ) {
      setFiredProjectLoadAnalytics(true);
      dispatch(waysToOrderLoad({project}));
    }
  }, [firedProjectLoadAnalytics, project, location.pathname, dispatch]);

  useEffect(() => {
    if (!product) {
      return;
    }

    function setDeliveryDates(data) {
      setStandardDelivery(data.shipping.standardDelivery.deliveryDate);
      data.shipping.superRushDelivery &&
        setRushDelivery(data.shipping.superRushDelivery.deliveryDate);
    }

    const quoteRequestParams = {
      quote_info: {
        postal_code: '22031',
        color_counts: {front: 1},
        items: [
          {
            product_id: product.id,
            quantity: product.minimum_quantity
          }
        ]
      }
    };

    window
      .fetch(remoteServices.price_quote, {
        ...postHeaders,
        credentials: rfeCredentialsHeader,
        body: JSON.stringify(quoteRequestParams)
      })
      .then(checkResponseOkay)
      .then(response => response.json())
      .then(data => setDeliveryDates(deserializeQuoteWidget(data.quote)))
      .catch(() => {
        const date = new Date();
        const deliveryDates = {
          shipping: {
            standardDelivery: {
              deliveryDate: addDays(
                date,
                product.turntime.standard
              ).toLocaleString('en-En', {
                weekday: 'long',
                month: 'short',
                day: 'numeric'
              })
            }
          }
        };

        if ('rush' in product.turntime) {
          deliveryDates['shipping']['superRushDelivery'] = {
            deliveryDate: addDays(date, product.turntime.rush).toLocaleString(
              'en-En',
              {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              }
            )
          };
        }
        setDeliveryDates(deliveryDates);
      });
  }, [product]);

  const shipToMultiple = context => context['shipping'] === 'COMMUNITY';
  const sizesKnown = context => context['size'] === 'ORGANIZER';

  useEffect(() => {
    const context = {
      shipping: project.shipping,
      size: project.sizes,
      payment: project.payment
    };
    const fulfillmentPlatformDecision = () =>
      decision(
        sizesKnown,
        decision(shipToMultiple, 'BSS', 'BULK'),
        decision(shipToMultiple, 'GOFIndividual', 'GOFBulk')
      );
    setFulfillmentPlatform(decide(context, fulfillmentPlatformDecision));
  }, [fulfillmentPlatform, project]);

  useEffect(() => {
    if (!project || project?.payment == null) return;

    let newSizes = project.sizes;
    if (isProductOneSize && project.payment === 'ORGANIZER') {
      newSizes = 'ORGANIZER';
    } else if (project.payment === 'COMMUNITY') {
      newSizes = 'COMMUNITY';
    }

    if (newSizes !== project.sizes) {
      setProject(prev => ({...prev, sizes: newSizes}));
    }
  }, [project, isProductOneSize]);

  const updateFormValues = (value, step) => {
    const updatedProject = {...project, [step]: value};
    setProject(updatedProject);
    dispatch(waysToOrderOptionUpdate({project: updatedProject}));
    metrics.fireEvent('ways to order select', value);
  };

  const handleBulk = () => {
    metrics.fireEvent(
      'ways to order options',
      `${project.shipping}-${project.sizes}-${project.payment}`
    );
    setShowSpinner(true);
    projectClient
      .saveProject(serialize(project, fulfillmentPlatform))
      .then(() => {
        if (!simplePricingEnabled) {
          const nextPath = '/next/quote';
          dispatch(setOrderOptionsPath(nextPath));
          navigate(nextPath);
        } else {
          const nextPath = oneSizeOneColor
            ? '/next/loadingDesignChecker'
            : '/next/quote';
          dispatch(setOrderOptionsPath(nextPath));
          navigate(nextPath);
        }
      });
  };

  const handleGOF = () => {
    metrics.pageViewTag('/ndx/orderoptions/gof');
    metrics.fireEvent(
      'ways to order options',
      `${project.shipping}-${project.sizes}-${project.payment}`
    );
    setShowSpinner(true);
    projectClient
      .saveProject(serialize(project, fulfillmentPlatform))
      .then(() => {
        if (orderOptionsPath !== '/next/gofQuote') {
          dispatch(setOrderOptionsPath('/next/gofQty'));
          navigate('/next/gofQty');
        } else {
          navigate(orderOptionsPath);
        }
      });
  };

  const onFundraiserClick = () => {
    ga4event({
      event: 'lab_interaction',
      lab_interaction_location: 'lab_quote_options',
      lab_interaction_type: 'start fundraiser button'
    });
    navigate('/next/fundraising');
  };

  const deliveryMessageProps = {
    BULK: {
      primaryText: (
        <>
          {GET_IT_BY}{' '}
          <span className="purple-text">
            {shortenWeekDay(standardDelivery)}
          </span>{' '}
          {isDateMoreThanTwoWeeksAway(standardDelivery)
            ? 'with FREE delivery'
            : 'with FREE\u00A02‑week\u00A0delivery'}
        </>
      ),
      secondaryText: rushDelivery && (
        <>
          or as soon as {rushDelivery} with Super Rush delivery (select options
          in&nbsp;cart)
        </>
      ),
      handleOnClick: handleBulk
    },
    BSS: {
      primaryText: (
        <>
          Get it as soon as{' '}
          <span className="purple-text">
            {shortenWeekDay(standardDelivery)}
          </span>{' '}
          by ordering today!
        </>
      ),
      secondaryText: 'First address free, each additional $9.95',
      handleOnClick: handleBulk
    },
    GOFBulk: {
      primaryText: (
        <>
          Get it as soon as{' '}
          <span className="purple-text">
            {shortenWeekDay(standardDelivery)}
          </span>{' '}
          by ordering today!
        </>
      ),
      secondaryText:
        'Collect sizes and shipping information from your\u00A0group',
      handleOnClick: handleGOF
    },
    GOFIndividual: {
      primaryText: (
        <>
          Get it as soon as{' '}
          <span className="purple-text">
            {shortenWeekDay(standardDelivery)}
          </span>{' '}
          by ordering today!
        </>
      ),
      secondaryText: 'Collect sizes and payments from your\u00A0group',
      handleOnClick: handleGOF
    }
  };

  const {shipping, sizes, payment} = project;

  const shippingOption = determineShippingOption(
    individualShipEligible,
    isProductOneSize
  );

  return (
    <>
      <div
        data-testid={preview ? 'order-options-preview' : 'order-options'}
        className={classnames('ndx-OrderOptions', {
          'is-Preview': preview
        })}>
        <Box>
          {showSpinner ? (
            <Spinner />
          ) : (
            <>
              {!preview ? (
                <Typography
                  variant="heading3"
                  className="ndx-OrderOptions-header">
                  Choose Your Order Options
                </Typography>
              ) : (
                <Typography
                  variant="uiTextSmall"
                  className="ndx-OrderOptions-header"
                  onClick={() => {
                    metrics.fireEvent(
                      'ways to order options',
                      'change options'
                    );
                    ga4event({
                      event: 'lab_interaction',
                      // right now preview is only shown in gof quote summary
                      lab_interaction_location: 'gof quote summary',
                      lab_interaction_type: 'change options link'
                    });
                    navigate('/next');
                  }}>
                  <ArrowNextIcon
                    fontSize="small"
                    className="ndx-OrderOptions-header-icon"
                  />
                  Change options
                </Typography>
              )}
              <div className="ndx-OrderOptions-steps">
                {individualShipEligible && (
                  <OrderOption
                    preview={preview}
                    step={'shipping'}
                    stepValue={shipping}
                    stepTitle={'Shipping'}
                    options={{
                      ORGANIZER: {
                        id: 'shipping_organizer',
                        text: 'Ship to single address'
                      },
                      COMMUNITY: {
                        id: 'shipping_community',
                        text: 'Ship to multiple addresses'
                      }
                    }}
                    onClick={updateFormValues}
                    project={project}
                    counter={'1'}
                  />
                )}
                {!isProductOneSize && (
                  <OrderOption
                    preview={preview}
                    step={'sizes'}
                    stepValue={sizes}
                    stepTitle={'Sizes and Quantities'}
                    options={{
                      ORGANIZER: {
                        id: 'sizes_organizer',
                        text: 'I know the sizes I\u00A0need'
                      },
                      COMMUNITY: {
                        id: 'sizes_community',
                        text: 'Invite my group to choose their sizes'
                      }
                    }}
                    onClick={updateFormValues}
                    project={project}
                    counter={individualShipEligible ? '2' : '1'}
                  />
                )}

                <OrderOption
                  preview={preview}
                  step={'payment'}
                  stepValue={payment}
                  stepTitle={'Payment'}
                  options={{
                    ORGANIZER: {
                      id: 'payment_organizer',
                      text: 'I will pay for the entire order'
                    },
                    COMMUNITY: {
                      id: 'payment_community',
                      text: 'Invite my group to pay for their order'
                    }
                  }}
                  onClick={updateFormValues}
                  project={project}
                  counter={shippingOption}
                />
              </div>
              <DeliveryMessage
                loading={standardDelivery === undefined}
                {...deliveryMessageProps[fulfillmentPlatform]}
              />
              {!preview && isWtoTestEnabled && (
                <Stack padding="1rem" gap="0.5rem">
                  <Typography weight="bold">
                    Want to raise funds instead?
                  </Typography>
                  <Typography>
                    Sell custom t-shirts and take donations with no inventory,
                    hassels or risk.
                  </Typography>
                  <Button
                    sx={{
                      alignSelf: 'center'
                    }}
                    variant="secondary"
                    onClick={onFundraiserClick}>
                    Start Fundraiser
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Box>
      </div>
      <Stack
        className="proceedButton"
        justifyContent="flex-end"
        sx={{
          px: 2,
          py: {sm: 1.5, md: 2},
          flexDirection:
            isWtoTestEnabled && breakpoint(MediaQueries.mediumAndBelow)
              ? 'column'
              : 'row',
          borderTop: '1px solid #e3e3e3',
          gap: '1rem'
        }}>
        <Button
          size="large"
          data-testid="order-options-continue"
          onClick={deliveryMessageProps[fulfillmentPlatform].handleOnClick}
          sx={{minWidth: '12rem'}}
          fullWidth={breakpoint(MediaQueries.onlySmallAndXSmall)}>
          {skipSizes ? 'Add to Cart' : 'Continue to Sizes'}
        </Button>
      </Stack>
    </>
  );
}

const propTypes = {
  preview: PropTypes.bool
};

OrderOptions.propTypes = propTypes;
